/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : GeneralInfoForm.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : General Info Form component
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Dec 30, 2021
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Grid, Button, Input, Badge } from "@scuf/common";
import { setCurrentWorkFlowStep } from "stores/ApmWorkFlowStore";
import { useForm } from "react-hook-form";
import { selectCustomer } from "stores/CustomerStore";
import { IGeneralInfoFormFields } from "models/generalInfo";
import {
  setGeneralInfo,
  selectGeneralInfoFormData,
  selectLoggedInUserContactDetails,
} from "stores/GeneralInfoStore";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

export const GeneralInfoForm: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const generalInfoFormData = useSelector(selectGeneralInfoFormData);
  const loggedInUserContactDetails = useSelector(
    selectLoggedInUserContactDetails
  );
  const selectedCustomer = useSelector(selectCustomer);

  const validationSchema = Yup.object({
    customerPhone: Yup.string()
      .required("Required")
      .min(10, "Expected minimum of 10 alphanumeric characters")
      .max(14, "Expected maximum of 14 alphanumeric characters")
      .matches(/^[a-zA-Z0-9-.+()/]*$/, {
        message: "Phone number must contain only [^a-zA-Z0-9 -.+()/]",
        excludeEmptyString: true,
      })
      .test("len", "Enter a valid phone number.", (val) => {
        let valid = false;
        valid = val
          ? val.length === val.split("0").length - 1
            ? false
            : true
          : false;
        return valid;
      }),

    customerEmail: Yup.string()
      .required("Required")
        .matches(/^[a-zA-Z0-9._-]+([+]?)([a-zA-Z0-9]?)+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, {
        message: "Enter a valid email address.",
        excludeEmptyString: true,
      }),
  }).required();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<IGeneralInfoFormFields>({
    defaultValues: {
      customerName: selectedCustomer?.customerName,
      customerEmail:
        generalInfoFormData?.customerEmail !== ""
          ? generalInfoFormData?.customerEmail
          : loggedInUserContactDetails?.email,
      customerPhone:
        generalInfoFormData?.customerPhone !== ""
          ? generalInfoFormData?.customerPhone
          : loggedInUserContactDetails?.phoneNo,
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const generalInfoFormFieldsRender = watch();

  useEffect(() => {
    register("customerName");
    register("customerPhone");
    register("customerEmail");
  }, [register]);

  const onSubmit = handleSubmit((data: IGeneralInfoFormFields) => {
    const generalInfoConfigValue = {
      ...data,
    };

    dispatch(setGeneralInfo(generalInfoConfigValue));
    dispatch(setCurrentWorkFlowStep(2));
  });

  const onClickBack = () => {
    navigate("/");
    dispatch(setCurrentWorkFlowStep(1));
  };

  return (
    <div>
      <form onSubmit={onSubmit}>
        <div className="general-info-header">
          <div className="reqiure-field-label">
            Enter the below details (Fields marked with
            <Badge
              color="red"
              empty={true}
              className="asterix-replacement"
            ></Badge>
            are required)
          </div>
          <div className="clear-button">
            <Button
              type="secondary"
              actionType="reset"
              content="Clear All"
              onClick={() => {
                reset({
                  customerName: selectedCustomer?.customerName,
                  customerPhone: "",
                  customerEmail: "",
                });
              }}
            />
          </div>
        </div>
        <Card>
          <Card.Header>
            <h4> General Information</h4>
          </Card.Header>
          <Card.Content>
            <Grid>
              <Grid.Row>
                <Grid.Column lWidth={4} xsWidth={12}>
                  <Input
                    indicator="required"
                    disabled={true}
                    label="Customer/Company Name"
                    name="customerName"
                    defaultValue={selectedCustomer?.customerName}
                  />
                </Grid.Column>
                <Grid.Column lWidth={4} xsWidth={12}>
                  <Input
                    placeholder="Enter Customer Phone"
                    indicator="required"
                    name="customerPhone"
                    onChange={(data) => {
                      setValue("customerPhone", data, {
                        shouldValidate: true,
                      });
                    }}
                    label="Customer Phone"
                    value={generalInfoFormFieldsRender?.customerPhone}
                    error={
                      errors && errors.customerPhone
                        ? errors.customerPhone.message
                        : ""
                    }
                  />
                </Grid.Column>
                <Grid.Column lWidth={4} xsWidth={12}>
                  <Input
                    placeholder="Enter Customer Email"
                    name="customerEmail"
                    indicator="required"
                    value={generalInfoFormFieldsRender?.customerEmail}
                    onChange={(data) => {
                      setValue("customerEmail", data, {
                        shouldValidate: true,
                      });
                    }}
                    label="Customer Email Address"
                    error={
                      errors && errors.customerEmail
                        ? errors.customerEmail.message
                        : ""
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Card.Content>
        </Card>
        <Grid className="bottom-button-grid">
          <Grid.Row>
            <Grid.Column lWidth={6} xsWidth={12}>
              <Button
                type="secondary"
                size="medium"
                content="Back"
                onClick={onClickBack}
              />
            </Grid.Column>
            <Grid.Column lWidth={6} xsWidth={12} className="next-button">
              <Button
                actionType="submit"
                type="primary"
                size="medium"
                content="Next"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </form>
    </div>
  );
};
